<script>
export default {
  name: "BanobagiMarketplacePanel",
  props: {
    channels: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ""
    }
  },
  emits: ["select-channel", "upload-receipt", "retry"],
  computed: {
    visibleChannels() {
      return this.channels.slice(0, 3);
    }
  },
  methods: {
    channelName(channel) {
      return (
        channel.marketplace ||
        channel.sale_channel_name ||
        channel.name ||
        channel.shopName ||
        this.$t("marketplace_channel")
      );
    },
    channelImage(channel) {
      return channel.marketplaceUrl || channel.image_url || channel.logo || "";
    },
    channelKey(channel, index) {
      return (
        channel.id ||
        `${channel.branchId || "branch"}-${
          channel.salesChannelId || channel.sale_channel_id || index
        }`
      );
    }
  }
};
</script>

<template>
  <section class="banobagi-marketplace" data-test="profile-marketplace-panel">
    <h2 class="banobagi-marketplace__title">
      {{ $t("marketplace_channel") }}
    </h2>

    <div
      v-if="isLoading"
      class="banobagi-marketplace__loading"
      data-test="profile-marketplace-loading"
      role="status"
      :aria-label="$t('loading')"
    >
      <span v-for="item in 4" :key="item"></span>
    </div>

    <div
      v-else-if="errorMessage"
      class="banobagi-marketplace__error"
      data-test="profile-marketplace-error-msg"
      role="alert"
    >
      <span>{{ errorMessage }}</span>
      <button
        type="button"
        class="banobagi-marketplace__retry"
        data-test="profile-marketplace-retry-btn"
        @click="$emit('retry')"
      >
        {{ $t("retry") }}
      </button>
    </div>

    <div v-else class="banobagi-marketplace__channels">
      <button
        v-if="visibleChannels.length === 0"
        type="button"
        class="banobagi-marketplace__channel"
        data-test="profile-marketplace-empty-btn"
        :aria-label="$t('btn_connect_market')"
        @click="$emit('select-channel', {})"
      >
        <span class="banobagi-marketplace__name">
          {{ $t("marketplace_channel") }}
        </span>
        <span class="banobagi-marketplace__icon-shell">
          <font-awesome-icon :icon="['fas', 'store-alt']" />
        </span>
        <span class="banobagi-marketplace__status">
          {{ $t("btn_connect_market") }}
        </span>
      </button>

      <button
        v-for="(channel, index) in visibleChannels"
        :key="channelKey(channel, index)"
        type="button"
        class="banobagi-marketplace__channel"
        :data-test="`profile-marketplace-channel-${index}-btn`"
        :aria-label="channelName(channel)"
        @click="$emit('select-channel', channel)"
      >
        <span class="banobagi-marketplace__name">
          {{ channelName(channel) }}
        </span>
        <span
          :class="[
            'banobagi-marketplace__icon-shell',
            { 'is-connected': channel.isConnected }
          ]"
        >
          <img
            v-if="channelImage(channel)"
            :src="channelImage(channel)"
            :alt="channelName(channel)"
            class="banobagi-marketplace__logo"
          />
          <font-awesome-icon v-else :icon="['fas', 'store-alt']" />
          <span
            v-if="channel.isConnected"
            class="banobagi-marketplace__check"
            aria-hidden="true"
          >
            <font-awesome-icon :icon="['fas', 'check']" />
          </span>
        </span>
        <span
          :class="[
            'banobagi-marketplace__status',
            { 'is-connected': channel.isConnected }
          ]"
        >
          {{
            channel.isConnected
              ? $t("btn_connect_success_market")
              : $t("btn_connect_market")
          }}
        </span>
      </button>

      <button
        type="button"
        class="banobagi-marketplace__channel"
        data-test="profile-marketplace-receipt-btn"
        :aria-label="$t('send_receipt')"
        @click="$emit('upload-receipt')"
      >
        <span class="banobagi-marketplace__name">
          {{ $t("your_receipt") }}
        </span>
        <span class="banobagi-marketplace__icon-shell is-receipt">
          <font-awesome-icon :icon="['fas', 'receipt']" />
        </span>
        <span class="banobagi-marketplace__status">
          {{ $t("send_receipt") }}
        </span>
      </button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.banobagi-marketplace {
  position: relative;
  z-index: 2;
  margin: 52px 16px 0;
  padding: 32px 12px 16px;

  border-radius: 28px;

  box-shadow: 0 14px 34px rgba(101, 74, 105, 0.16);
  backdrop-filter: blur(16px);
}

.banobagi-marketplace__title {
  position: absolute;
  top: -24px;
  left: 0;
  width: 100%;
  margin: 0;
  padding: 12px;
  border-radius: var(--crm-border-radius);
  background: linear-gradient(
    90deg,
    rgb(249 191 234 / 41%) 0%,
    rgb(92 167 220 / 35%) 100%
  );
  color: var(--primary-color);
  font-size: var(--font-title-sm);
  text-align: center;
}

.banobagi-marketplace__channels,
.banobagi-marketplace__loading {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.banobagi-marketplace__error {
  display: flex;
  min-height: 86px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--text-color);
  text-align: center;
}

.banobagi-marketplace__retry {
  min-width: 88px;
  min-height: 40px;
  padding: 8px 16px;
  border: 1px solid var(--primary-color);
  border-radius: var(--crm-border-radius);
  background: transparent;
  color: var(--primary-color);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}

.banobagi-marketplace__retry:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.banobagi-marketplace__channel {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: var(--text-color);
  cursor: pointer;
  font: inherit;
}

.banobagi-marketplace__channel:focus-visible {
  border-radius: 16px;
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.banobagi-marketplace__name,
.banobagi-marketplace__status {
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.banobagi-marketplace__name {
  font-size: var(--font-body);
  // font-weight: 600;
}

.banobagi-marketplace__icon-shell {
  position: relative;
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  overflow: visible;
  // border: 5px solid var(--light-color);
  border-radius: 50%;
  background: var(--disable-color);
  color: #aaa3b2;
  font-size: var(--font-heading);
  box-shadow: inset 0 0 0 1px rgba(105, 89, 112, 0.08);
}

.banobagi-marketplace__icon-shell.is-connected {
  // border-color: var(--light-color);
  background: var(--secondary-color);
}

.banobagi-marketplace__icon-shell.is-receipt {
  // border-color: var(--light-color-low);
  background: var(--disable-color);
  color: var(--font-disable-color);
}

.banobagi-marketplace__logo {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.banobagi-marketplace__check {
  position: absolute;
  right: -4px;
  bottom: -3px;
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--secondary-color);
  border-radius: 50%;
  background: #2dc45a;
  color: #ffffff;
  font-size: var(--font-helper);
}

.banobagi-marketplace__status {
  min-height: 18px;
  color: var(--primary-color);
  font-size: var(--font-body-sm);
}

.banobagi-marketplace__status.is-connected {
  color: #278f4b;
}

.banobagi-marketplace__loading span {
  height: 86px;
  border-radius: 18px;
  background: linear-gradient(
    110deg,
    var(--disable-color) 30%,
    var(--secondary-color) 50%,
    var(--disable-color) 70%
  );
  background-size: 200% 100%;
  animation: banobagi-marketplace-shimmer 1.2s linear infinite;
}

@keyframes banobagi-marketplace-shimmer {
  to {
    background-position-x: -200%;
  }
}

@media (max-width: 360px) {
  .banobagi-marketplace {
    margin-right: 12px;
    margin-left: 12px;
    padding-right: 8px;
    padding-left: 8px;
  }

  .banobagi-marketplace__icon-shell {
    width: 48px;
    height: 48px;
  }
}
</style>
